import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import * as sessionApi from "@/api/sessionApi";
import * as processingUtils from "@/utils/processingUtils";
import type { Stem } from "@/types/stem";
import type { SessionType } from "@/api/sessionApi";
import { isSessionExpired, EXPIRED_MESSAGE } from "@/utils/sessionExpiry";

// Helper functions for donation modal tracking
const hasShownDonationModal = (sessionId: string): boolean => {
  const shown = localStorage.getItem("donation-modal-shown");
  if (!shown) return false;
  try {
    const shownIds = JSON.parse(shown);
    return shownIds.includes(sessionId);
  } catch {
    return false;
  }
};

const markDonationModalShown = (sessionId: string) => {
  const shown = localStorage.getItem("donation-modal-shown") || "[]";
  try {
    const shownIds = JSON.parse(shown);
    if (!shownIds.includes(sessionId)) {
      shownIds.push(sessionId);
      localStorage.setItem("donation-modal-shown", JSON.stringify(shownIds));
    }
  } catch {
    localStorage.setItem("donation-modal-shown", JSON.stringify([sessionId]));
  }
};

const errorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : "Failed to process audio file";

export const useStemSession = () => {
  const queryClient = useQueryClient();
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [processingDuration, setProcessingDuration] = useState(0);
  const [processingError, setProcessingError] = useState<string | null>(null);
  const [_activeSession, _setActiveSession] = useState<{ id: string | null; type: SessionType }>({
    id: null,
    type: "stem",
  });
  const [isDownloading, setIsDownloading] = useState(false);
  const [showDonationModal, setShowDonationModal] = useState(false);
  const [donationModalSessionId, setDonationModalSessionId] = useState<string | null>(null);
  const [dismissedForSession, setDismissedForSession] = useState<string | null>(null);
  const [showDonationBanner, setShowDonationBanner] = useState(false);
  const [dismissedBannerForSession, setDismissedBannerForSession] = useState<string | null>(null);

  // Custom setter that clears dismissed state when navigating to a different session
  const setActiveSession: React.Dispatch<React.SetStateAction<{ id: string | null; type: SessionType }>> = (value) => {
    const newSession = typeof value === "function" ? value(_activeSession) : value;
    // Clear dismissed state when navigating to a different session
    if (newSession.id !== _activeSession.id) {
      setDismissedForSession(null);
      setDismissedBannerForSession(null);
    }
    _setActiveSession(newSession);
  };

  const activeSession = _activeSession;

  const { data: sessions, isLoading: isLoadingStemSessions } = useQuery({
    queryKey: ["stemSessions", "stem"],
    queryFn: () => sessionApi.fetchSessions("stem"),
  });

  const { data: acapellaSessions, isLoading: isLoadingAcapellaSessions } = useQuery({
    queryKey: ["stemSessions", "acapella"],
    queryFn: () => sessionApi.fetchSessions("acapella"),
  });

  const { data: drumSessions, isLoading: isLoadingDrumSessions } = useQuery({
    queryKey: ["stemSessions", "drum"],
    queryFn: () => sessionApi.fetchSessions("drum"),
  });

  const isLoadingSessions = isLoadingStemSessions || isLoadingAcapellaSessions || isLoadingDrumSessions;

  // Check for completed sessions on page load/navigation - DISABLED
  // useEffect(() => {
  //   if (isLoadingSessions) return;
  //
  //   // Get the current session type based on active session
  //   let currentTypeSessions: any[] = [];
  //   let currentSessionType: SessionType = activeSession.type;
  //
  //   switch (activeSession.type) {
  //     case 'stem':
  //       currentTypeSessions = sessions || [];
  //       break;
  //     case 'acapella':
  //       currentTypeSessions = acapellaSessions || [];
  //       break;
  //     case 'drum':
  //       currentTypeSessions = drumSessions || [];
  //       break;
  //   }
  //
  //   // Find completed sessions that haven't shown donation modal for current page type
  //   const completedSession = currentTypeSessions.find(session =>
  //     session.status === 'completed' &&
  //     session.stems &&
  //     session.stems.length > 0 &&
  //     !hasShownDonationModal(session.id)
  //   );
  //
  //   if (completedSession && !showDonationModal) {
  //     setDonationModalSessionId(completedSession.id);
  //     setShowDonationModal(true);
  //   }
  // }, [sessions, acapellaSessions, drumSessions, isLoadingSessions, showDonationModal, activeSession.type]);

  // Show modal on every session navigation - DISABLED
  // useEffect(() => {
  //   // Don't trigger on initial load or when no session is selected
  //   if (!activeSession.id || isLoadingSessions) return;
  //
  //   // Don't show if user dismissed modal for this session
  //   if (dismissedForSession === activeSession.id) return;
  //
  //   // Get sessions for the current tool type
  //   let currentTypeSessions: any[] = [];
  //
  //   switch (activeSession.type) {
  //     case 'stem':
  //       currentTypeSessions = sessions || [];
  //       break;
  //     case 'acapella':
  //       currentTypeSessions = acapellaSessions || [];
  //       break;
  //     case 'drum':
  //       currentTypeSessions = drumSessions || [];
  //       break;
  //   }
  //
  //   // Check if there are ANY completed sessions (don't check localStorage)
  //   const hasCompletedSession = currentTypeSessions.some(session =>
  //     session.status === 'completed' &&
  //     session.stems &&
  //     session.stems.length > 0
  //   );
  //
  //   // Show modal if there are completed sessions
  //   if (hasCompletedSession && !showDonationModal) {
  //     // Use the first completed session ID for tracking
  //     const completedSession = currentTypeSessions.find(session =>
  //       session.status === 'completed' &&
  //       session.stems &&
  //       session.stems.length > 0
  //     );
  //
  //     if (completedSession) {
  //       setDonationModalSessionId(completedSession.id);
  //       setShowDonationModal(true);
  //     }
  //   }
  // }, [activeSession.id, sessions, acapellaSessions, drumSessions, isLoadingSessions, showDonationModal, dismissedForSession]);

  // Show banner on session navigation for completed sessions
  useEffect(() => {
    // Don't trigger on initial load or when no session is selected
    if (!activeSession.id || isLoadingSessions) return;

    // Don't show if user dismissed banner for this session
    if (dismissedBannerForSession === activeSession.id) return;

    // Get sessions for the current tool type
    let currentTypeSessions: any[] = [];

    switch (activeSession.type) {
      case "stem":
        currentTypeSessions = sessions || [];
        break;
      case "acapella":
        currentTypeSessions = acapellaSessions || [];
        break;
      case "drum":
        currentTypeSessions = drumSessions || [];
        break;
    }

    // Find the current session
    const currentSession = currentTypeSessions.find((session) => session.id === activeSession.id);

    // Show banner if current session is completed and not expired
    if (
      currentSession?.status === "completed" &&
      currentSession?.stems &&
      currentSession.stems.length > 0 &&
      !isSessionExpired(currentSession)
    ) {
      setShowDonationBanner(true);
    } else {
      setShowDonationBanner(false);
    }
  }, [activeSession.id, sessions, acapellaSessions, drumSessions, isLoadingSessions, dismissedBannerForSession]);

  const createSession = useMutation({
    mutationFn: sessionApi.createSession,
    onSuccess: (newSession) => {
      queryClient.invalidateQueries({ queryKey: ["stemSessions", newSession.type] });
      setActiveSession({ id: newSession.id, type: newSession.type as SessionType });
    },
    onError: (error) => {
      console.error("Error in createSession mutation:", error);
      toast.error("Failed to create session");
    },
  });

  const updateSession = useMutation({
    mutationFn: sessionApi.updateSession,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["stemSessions"] });
    },
    onError: (error) => {
      console.error("Error in updateSession mutation:", error);
      toast.error("Failed to update session");
    },
  });

  const deleteSession = useMutation({
    mutationFn: ({ id }: { id: string }) => sessionApi.deleteSession(id),
    onSuccess: (_, { id }) => {
      if (activeSession.id === id) {
        setActiveSession({ id: null, type: activeSession.type });
      }
      queryClient.invalidateQueries({ queryKey: ["stemSessions"] }).then(() => {
        const remainingSessions =
          activeSession.type === "stem"
            ? sessions?.filter((s) => s.id !== id)
            : activeSession.type === "acapella"
              ? acapellaSessions?.filter((s) => s.id !== id)
              : drumSessions?.filter((s) => s.id !== id);
        if (remainingSessions?.length > 0 && activeSession.id === id) {
          setActiveSession({ id: remainingSessions[0].id, type: activeSession.type });
        }
      });
      toast.success("Session deleted successfully");
    },
    onError: (error) => {
      console.error("Error in deleteSession mutation:", error);
      toast.error("Failed to delete session");
    },
  });

  const retryProcessing = async (sessionId: string) => {
    const stemSession = sessions?.find((s) => s.id === sessionId);
    const acapellaSession = acapellaSessions?.find((s) => s.id === sessionId);
    const drumSession = drumSessions?.find((s) => s.id === sessionId);
    const session = stemSession || acapellaSession || drumSession;
    const type = stemSession ? "stem" : acapellaSession ? "acapella" : "drum";

    if (!session) {
      toast.error("Session not found");
      return;
    }

    if (isSessionExpired(session)) {
      toast.info(EXPIRED_MESSAGE);
      return;
    }

    let progressInterval: ReturnType<typeof setInterval> | undefined;
    try {
      setIsProcessing(true);
      setProgress(0);
      setProcessingError(null);

      const processingTime = await processingUtils.getProcessingDuration(session.original_audio_url);
      setProcessingDuration(processingTime);

      // New progress phases
      const initialPhaseDuration = processingTime * 0.3; // 30% of time for 0-60% progress
      const midPhaseDuration = processingTime * 0.3; // 30% of time for 60-90% progress
      const tertiaryPhaseDuration = processingTime * 0.2; // 20% of time for 90-95% progress
      const finalPhaseDuration = processingTime * 0.2; // 20% of time for 95-99% progress

      // Calculate rates for each phase
      const initialRate = 60 / initialPhaseDuration; // 0-60%
      const midRate = 30 / midPhaseDuration; // 60-90%
      const tertiaryRate = 5 / tertiaryPhaseDuration; // 90-95%
      const finalRate = 4 / finalPhaseDuration; // 95-99%

      let currentProgress = 0;
      const startTime = Date.now();

      progressInterval = setInterval(() => {
        const elapsedSeconds = (Date.now() - startTime) / 1000;

        if (elapsedSeconds < initialPhaseDuration) {
          // Initial phase: 0-60%
          currentProgress = elapsedSeconds * initialRate;
        } else if (elapsedSeconds < initialPhaseDuration + midPhaseDuration) {
          // Middle phase: 60-90%
          currentProgress = 60 + (elapsedSeconds - initialPhaseDuration) * midRate;
        } else if (elapsedSeconds < initialPhaseDuration + midPhaseDuration + tertiaryPhaseDuration) {
          // Tertiary phase: 90-95%
          currentProgress = 90 + (elapsedSeconds - initialPhaseDuration - midPhaseDuration) * tertiaryRate;
        } else if (
          elapsedSeconds <
          initialPhaseDuration + midPhaseDuration + tertiaryPhaseDuration + finalPhaseDuration
        ) {
          // Final phase: 95-99%
          currentProgress =
            95 + (elapsedSeconds - initialPhaseDuration - midPhaseDuration - tertiaryPhaseDuration) * finalRate;
        } else {
          // Cap at 99%
          currentProgress = 99;
        }

        // Cap at 99% and round to ensure clean integer display
        setProgress(Math.min(Math.round(currentProgress), 99));
      }, 200);

      await updateSession.mutateAsync({
        id: sessionId,
        stems: (session.stems as Stem[]) || [],
        status: "processing",
      });

      const processedStems = await processingUtils.processStemSplit(
        session.original_audio_url,
        type === "acapella" ? "aggregate-vocals" : type === "drum" ? "aggregate-drumsep" : "aggregate-6stem-v2",
      );
      const processing_minutes = processedStems.length;

      await updateSession.mutateAsync({
        id: sessionId,
        stems: processedStems,
        status: "completed",
        processing_minutes,
      });

      setProgress(100);
      toast.success("Your audio was processed successfully");
    } catch (error) {
      console.error("Error processing audio:", error);
      const message = errorMessage(error);
      try {
        await updateSession.mutateAsync({
          id: sessionId,
          stems: (session.stems as Stem[]) || [],
          status: "failed",
        });
      } catch (updateError) {
        console.error("Failed to persist processing failure:", updateError);
      }
      setProcessingError(message);
      toast.error(message);
    } finally {
      if (progressInterval) clearInterval(progressInterval);
      setIsProcessing(false);
    }
  };

  const handleFileSelect = async (file: File, type: SessionType) => {
    let progressInterval: ReturnType<typeof setInterval> | undefined;
    let createdSessionId: string | null = null;
    let originalUrl: string | null = null;
    try {
      setIsProcessing(true);
      setProgress(0);
      setProcessingError(null);

      originalUrl = URL.createObjectURL(file);
      const processingTime = await processingUtils.getProcessingDuration(originalUrl);
      setProcessingDuration(processingTime);

      const existingSession = await sessionApi.checkExistingSession(file.name, type);
      if (existingSession) {
        throw new Error("This file is already being processed");
      }

      // New progress phases
      const initialPhaseDuration = processingTime * 0.3; // 30% of time for 0-60% progress
      const midPhaseDuration = processingTime * 0.3; // 30% of time for 60-90% progress
      const tertiaryPhaseDuration = processingTime * 0.2; // 20% of time for 90-95% progress
      const finalPhaseDuration = processingTime * 0.2; // 20% of time for 95-99% progress

      // Calculate rates for each phase
      const initialRate = 60 / initialPhaseDuration; // 0-60%
      const midRate = 30 / midPhaseDuration; // 60-90%
      const tertiaryRate = 5 / tertiaryPhaseDuration; // 90-95%
      const finalRate = 4 / finalPhaseDuration; // 95-99%

      let currentProgress = 0;
      const startTime = Date.now();

      progressInterval = setInterval(() => {
        const elapsedSeconds = (Date.now() - startTime) / 1000;

        if (elapsedSeconds < initialPhaseDuration) {
          // Initial phase: 0-60%
          currentProgress = elapsedSeconds * initialRate;
        } else if (elapsedSeconds < initialPhaseDuration + midPhaseDuration) {
          // Middle phase: 60-90%
          currentProgress = 60 + (elapsedSeconds - initialPhaseDuration) * midRate;
        } else if (elapsedSeconds < initialPhaseDuration + midPhaseDuration + tertiaryPhaseDuration) {
          // Tertiary phase: 90-95%
          currentProgress = 90 + (elapsedSeconds - initialPhaseDuration - midPhaseDuration) * tertiaryRate;
        } else if (
          elapsedSeconds <
          initialPhaseDuration + midPhaseDuration + tertiaryPhaseDuration + finalPhaseDuration
        ) {
          // Final phase: 95-99%
          currentProgress =
            95 + (elapsedSeconds - initialPhaseDuration - midPhaseDuration - tertiaryPhaseDuration) * finalRate;
        } else {
          // Cap at 99%
          currentProgress = 99;
        }

        // Cap at 99% and round to ensure clean integer display
        setProgress(Math.min(Math.round(currentProgress), 99));
      }, 200);

      const publicUrl = await processingUtils.uploadFile(file);
      console.log("File uploaded, creating session...");

      const newSession = await createSession.mutateAsync({
        original_filename: file.name,
        original_audio_url: publicUrl,
        status: "processing",
        type,
      });
      createdSessionId = newSession.id;

      setActiveSession({ id: newSession.id, type });
      console.log("Session created, processing stems...");

      const processedStems = await processingUtils.processStemSplit(
        publicUrl,
        type === "acapella" ? "aggregate-vocals" : type === "drum" ? "aggregate-drumsep" : "aggregate-6stem-v2",
      );
      console.log("Stems processed, updating session...");

      const processing_minutes = processedStems.length;

      await updateSession.mutateAsync({
        id: newSession.id,
        stems: processedStems,
        status: "completed",
        processing_minutes,
      });

      setProgress(100);
      toast.success("Your audio was processed successfully");
    } catch (error) {
      console.error("Error processing audio:", error);
      const message = errorMessage(error);
      if (createdSessionId) {
        try {
          await updateSession.mutateAsync({
            id: createdSessionId,
            stems: [],
            status: "failed",
          });
        } catch (updateError) {
          console.error("Failed to persist processing failure:", updateError);
        }
      }
      setProcessingError(message);
      toast.error(message);
    } finally {
      if (progressInterval) clearInterval(progressInterval);
      if (originalUrl) URL.revokeObjectURL(originalUrl);
      setIsProcessing(false);
    }
  };

  const handleDonationModalClose = () => {
    if (donationModalSessionId) {
      markDonationModalShown(donationModalSessionId);
    }
    if (activeSession.id) {
      setDismissedForSession(activeSession.id);
    }
    setShowDonationModal(false);
    setDonationModalSessionId(null);
  };

  const handleBannerClose = () => {
    if (activeSession.id) {
      setDismissedBannerForSession(activeSession.id);
    }
    setShowDonationBanner(false);
  };

  return {
    sessions,
    acapellaSessions,
    drumSessions,
    isLoadingSessions,
    createSession,
    updateSession,
    deleteSession,
    isProcessing,
    setIsProcessing,
    progress,
    setProgress,
    processingDuration,
    setProcessingDuration,
    processingError,
    setProcessingError,
    handleFileSelect,
    activeSession,
    setActiveSession,
    retryProcessing,
    isDownloading,
    setIsDownloading,
    showDonationModal,
    donationModalSessionId,
    handleDonationModalClose,
    showDonationBanner,
    handleBannerClose,
  };
};
